/**
 * Блокирующие системные состояния.
 *
 * Таблица обработчиков CLIENT_WEB.md §2.3:
 *   SYS-03 client_upgrade_required — единственное действие «Перезагрузить»;
 *   SYS-05 consent_required        — согласия, reconnect приостановлен;
 *   SYS-06 step_up_required        — подтверждение кодом, затем повтор операции;
 *   SYS-07 session_expired         — вход заново, локальные данные СОХРАНЯЮТСЯ;
 *   SYS-08 account_restricted/suspended;
 *   SYS-09 account_pending_deletion;
 *   SYS-10 csrf_rejected           — диагностика с X-Request-Id.
 */
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { api, getErrorTrace, unwrap } from '@shared/api/client'
import { useModalLayer } from '@shared/lib/modal-layer'
import { syncEngine } from '@shared/sync/instance'
import { Button, CodeInput, Field, Icon, useToast } from '@shared/ui'

import { refreshMe } from '../../boot/bootstrap'
import { logoutAndNavigate } from '../../boot/session-lifecycle'
import { useAppStore } from '../../model/app-store'
import { LegalLinks } from '../legal/LegalLinks'
import styles from './system.module.css'

export function BlockingScreens(): React.ReactElement | null {
  const blocking = useAppStore((s) => s.blocking)
  if (!blocking) return null

  return (
    <BlockingLayer>
      {blocking.kind === 'client_upgrade_required' ? <UpgradeRequired /> : null}
      {blocking.kind === 'consent_required' ? <ConsentRequired /> : null}
      {blocking.kind === 'step_up_required' ? <StepUpRequired retry={blocking.retry} /> : null}
      {blocking.kind === 'session_expired' ? <SessionExpired /> : null}
      {blocking.kind === 'account_restricted' || blocking.kind === 'account_suspended' ? (
        <AccountRestricted suspended={blocking.kind === 'account_suspended'} />
      ) : null}
      {blocking.kind === 'account_pending_deletion' ? <AccountPendingDeletion /> : null}
      {blocking.kind === 'diagnostic' ? (
        <Diagnostic code={blocking.code} requestId={blocking.requestId} />
      ) : null}
    </BlockingLayer>
  )
}

/**
 * Блокирующий экран — модальный слой без закрытия: интерфейс под ним
 * недоступен (`inert`), фокус остаётся внутри, `Escape` и «назад» не
 * закрывают его — снять состояние может только действие на самом экране.
 */
function BlockingLayer({ children }: { children: React.ReactNode }): React.ReactElement {
  const { ref, layerRef } = useModalLayer(true, () => undefined, { escape: false, history: false })

  return (
    <div ref={layerRef} data-modal-layer>
      <div ref={ref} className={styles['blocking']} role="dialog" aria-modal="true" tabIndex={-1}>
        {children}
      </div>
    </div>
  )
}

/* ===================================================================== */
/* SYS-03                                                                 */
/* ===================================================================== */

function UpgradeRequired(): React.ReactElement {
  return (
    <div className={styles['blockingBody']}>
      <span className={styles['icon']}>
        <Icon name="refresh" size={30} />
      </span>

      <div>
        <div className={styles['title']}>Требуется обновление</div>
        <p className={styles['text']}>
          Версия приложения устарела и больше не поддерживается. Перезагрузите страницу — обновление
          установится само.
        </p>
      </div>

      {/* §9.5 п. 4: перезагрузка — единственное доступное действие */}
      <div className={styles['actions']}>
        <Button variant="primary" size="lg" onClick={() => location.reload()}>
          Перезагрузить
        </Button>
      </div>
    </div>
  )
}

/* ===================================================================== */
/* SYS-05                                                                 */
/* ===================================================================== */

function ConsentRequired(): React.ReactElement {
  const config = useAppStore((s) => s.config)
  const setBlocking = useAppStore((s) => s.setBlocking)
  const toast = useToast()
  const [saving, setSaving] = useState(false)

  const accept = async (): Promise<void> => {
    if (!config) return
    setSaving(true)

    try {
      await api.POST('/me/consents', {
        body: {
          documents: [
            { document: 'terms', version: config.legal.terms.version },
            { document: 'privacy', version: config.legal.privacy.version },
            { document: 'community_rules', version: config.legal.community_rules.version },
          ],
        } as never,
      })

      await refreshMe()
      setBlocking(null)
      // BS §15.1: после принятия WebSocket переоткрывается.
      syncEngine()?.resumeAfterConsent()
    } catch {
      toast.show('Не удалось принять документы', { tone: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles['blockingBody']}>
      <span className={styles['icon']}>
        <Icon name="shield" size={30} />
      </span>

      <div>
        <div className={styles['title']}>Документы обновились</div>
        <p className={styles['text']}>
          Чтобы продолжить пользоваться сервисом, примите обновлённые условия, политику приватности и правила
          сообщества. Прочитать их можно здесь же.
        </p>
      </div>

      <LegalLinks />

      <div className={styles['actions']}>
        <Button variant="primary" size="lg" loading={saving} onClick={() => void accept()}>
          Принять и продолжить
        </Button>
      </div>
    </div>
  )
}

/* ===================================================================== */
/* SYS-06                                                                 */
/* ===================================================================== */

function StepUpRequired({ retry }: { retry: () => void }): React.ReactElement {
  const setBlocking = useAppStore((s) => s.setBlocking)
  const toast = useToast()

  const [challengeId, setChallengeId] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)

  const request = async (): Promise<void> => {
    setBusy(true)
    try {
      const challenge = unwrap(await api.POST('/auth/step-up', { body: {} as never }))
      setChallengeId(challenge.challenge_id)
    } catch {
      toast.show('Не удалось отправить код', { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const verify = async (): Promise<void> => {
    if (!challengeId) return
    setBusy(true)

    try {
      await api.POST('/auth/challenges/{challengeId}/verify', {
        params: { path: { challengeId } },
        body: { code } as never,
      })

      setBlocking(null)
      // §2.3: исходный запрос повторяется ровно один раз.
      retry()
    } catch {
      toast.show('Неверный код', { tone: 'danger' })
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles['blockingBody']}>
      <span className={styles['icon']}>
        <Icon name="lock" size={30} />
      </span>

      <div>
        <div className={styles['title']}>Подтвердите действие</div>
        <p className={styles['text']}>Отправим код на ваш способ входа — введите его, чтобы продолжить.</p>
      </div>

      <div className={styles['actions']}>
        {challengeId ? (
          <>
            <Field>{({ id }) => <CodeInput id={id} value={code} onValueChange={setCode} autoFocus />}</Field>
            <Button
              variant="primary"
              size="lg"
              loading={busy}
              disabled={code.length < 6}
              onClick={() => void verify()}
            >
              Подтвердить
            </Button>
          </>
        ) : (
          <Button variant="primary" size="lg" loading={busy} onClick={() => void request()}>
            Отправить код
          </Button>
        )}

        <Button variant="ghost" onClick={() => setBlocking(null)}>
          Отмена
        </Button>
      </div>
    </div>
  )
}

/* ===================================================================== */
/* SYS-07                                                                 */
/* ===================================================================== */

function SessionExpired(): React.ReactElement {
  const navigate = useNavigate()
  const setBlocking = useAppStore((s) => s.setBlocking)
  const setPhase = useAppStore((s) => s.setPhase)

  return (
    <div className={styles['blockingBody']}>
      <span className={styles['icon']}>
        <Icon name="lock" size={30} />
      </span>

      <div>
        <div className={styles['title']}>Нужно войти снова</div>
        <p className={styles['text']}>
          Срок сессии истёк. Черновики и неотправленные сообщения сохранены — они уйдут после входа.
        </p>
      </div>

      <div className={styles['actions']}>
        <Button
          variant="primary"
          size="lg"
          onClick={() => {
            setBlocking(null)
            setPhase('unauthenticated')
            void navigate({ to: '/auth' })
          }}
        >
          Войти
        </Button>
      </div>
    </div>
  )
}

/* ===================================================================== */
/* SYS-08 и SYS-09                                                        */
/* ===================================================================== */

function AccountRestricted({ suspended }: { suspended: boolean }): React.ReactElement {
  const navigate = useNavigate()
  const setBlocking = useAppStore((s) => s.setBlocking)

  return (
    <div className={styles['blockingBody']}>
      <span className={styles['icon']}>
        <Icon name="shield" size={30} />
      </span>

      <div>
        <div className={styles['title']}>{suspended ? 'Аккаунт приостановлен' : 'Доступ ограничен'}</div>
        <p className={styles['text']}>
          {/* ПС §14: без раскрытия внутренних антифрод-сигналов */}
          Мы не можем показать подробности здесь. Если считаете, что это ошибка, напишите в поддержку — мы
          разберёмся.
        </p>
      </div>

      <div className={styles['actions']}>
        <Button
          variant="primary"
          size="lg"
          onClick={() => {
            setBlocking(null)
            void navigate({ to: '/support/cant-login' })
          }}
        >
          Написать в поддержку
        </Button>

        <Button
          variant="ghost"
          onClick={() => void logoutAndNavigate(() => navigate({ to: '/', replace: true }))}
        >
          Выйти
        </Button>
      </div>
    </div>
  )
}

function AccountPendingDeletion(): React.ReactElement {
  const navigate = useNavigate()

  return (
    <div className={styles['blockingBody']}>
      <span className={styles['icon']}>
        <Icon name="trash" size={30} />
      </span>

      <div>
        <div className={styles['title']}>Аккаунт готовится к удалению</div>
        <p className={styles['text']}>
          Пока идёт срок восстановления, вход в сервис недоступен. Если передумали — напишите в поддержку,
          аккаунт можно вернуть.
        </p>
      </div>

      <div className={styles['actions']}>
        <Button variant="primary" size="lg" onClick={() => void navigate({ to: '/support/cant-login' })}>
          Восстановить аккаунт
        </Button>

        <Button
          variant="ghost"
          onClick={() => void logoutAndNavigate(() => navigate({ to: '/', replace: true }))}
        >
          Выйти
        </Button>
      </div>
    </div>
  )
}

/* ===================================================================== */
/* SYS-10                                                                 */
/* ===================================================================== */

function Diagnostic({ code, requestId }: { code: string; requestId: string }): React.ReactElement {
  const setBlocking = useAppStore((s) => s.setBlocking)
  const [traceOpen, setTraceOpen] = useState(false)

  return (
    <div className={styles['blockingBody']}>
      <span className={styles['icon']}>
        <Icon name="alert" size={30} />
      </span>

      <div>
        <div className={styles['title']}>Запрос отклонён</div>
        <p className={styles['text']}>
          Похоже, запрос изменило расширение браузера или прокси. Попробуйте открыть сервис в обычном окне без
          расширений.
        </p>
        {/* CC §10.2: технический код — только в диагностике, не в основном сообщении */}
        <p className={styles['requestId']}>
          {code} · {requestId}
        </p>
      </div>

      <div className={styles['actions']}>
        <Button variant="primary" size="lg" onClick={() => location.reload()}>
          Перезагрузить
        </Button>
        <Button variant="ghost" onClick={() => setBlocking(null)}>
          Продолжить
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setTraceOpen((open) => !open)}>
          {traceOpen ? 'Скрыть подробности' : 'Показать подробности'}
        </Button>
      </div>

      {/*
        CC §12.3: в буфере только код, статус, `X-Request-Id`, путь и время —
        тела запросов и ответов туда не попадают и попасть не могут.
      */}
      {traceOpen ? (
        <ul className={styles['trace']}>
          {getErrorTrace().length === 0 ? <li>Других ошибок не было.</li> : null}
          {getErrorTrace().map((entry) => (
            <li key={`${entry.at}:${entry.requestId}`}>
              {entry.method} {entry.path} · {entry.status} {entry.code} · {entry.requestId}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
